// DropPin live messages — v0.276.0
// ---------------------------------------------------------------------
// Brett, for a Teams meeting with a client: "if we both have it open, a client
// could attach a photo and send it to me while im waiting, or text instructions
// while we are talking." Agreed conditions:
//   1. never wake the DropPin database: the page polls a small blob marker
//      (/api/live-poll), and reads the database only for an item the marker
//      says changed;
//   2. stop polling while the tab is hidden, or after 15 minutes with nobody
//      touching the page (any touch, key or mouse move starts it again);
//   3. (slice 2) unseen messages still go out with Send update.
//
// v0.278.0 — PRESENCE. Brett: "could the client see like a thinking symbol and
// brett is editing if they happen to go in the same item?" The office's open
// item beats a few words into the same blob (presenceBeacon, below); the
// client's poll answer carries them, so the client sees "<name> is editing
// this item" / "is typing a reply". Still the blob only: a presence change
// never re-reads a thread (onChange fires only when an item's count moves).
//
//   watchLive({ pass, onChange, onState, onPresence, getPass, since }) → { stop(), pollNow() }
//     pass      the page's live pass (public-report hands the client one,
//               /api/live-token the office)
//     onChange  (defectIds) → called with the items whose thread changed
//     onState   ('live' | 'retrying' | 'paused' | 'ended' | 'off') → the page
//               says which, beside the conversation
//     onPresence  ({ defectId: [{ name, typing, who, chat }] }) → who is on
//               each item now; called only when that changes, and with {} the
//               moment the watch is not live (no stale "editing"). v0.284.0:
//               the office hears it too; who = 'office' | 'link', chat =
//               'client' | 'trade' (the chat on that person's screen)
//     getPass   office only: mints a new pass when the old one is refused
//     since     page load time (ms); the FIRST answer reports items changed
//               from a minute before it, so a message sent while the page was
//               loading is not missed
//
// Every state is named: 'off' = the server has no live set up (503), 'ended'
// = the pass is refused and no new one can be had (reload), 'retrying' = the
// poll is failing and backing off (10 s, then 30 s), 'paused' = idle 15 min.
//
// Safe to import under node (the tests do): pass doc / win / fetchImpl / now /
// setTimer / clearTimer, or nothing is watched for visibility or activity.

const hasDoc = typeof document !== 'undefined' && typeof document.addEventListener === 'function';
const hasWin = typeof window !== 'undefined' && typeof window.addEventListener === 'function';

export const LIVE_POLL_MS = 3000;
export const LIVE_IDLE_MS = 15 * 60 * 1000;
export const LIVE_BACKOFF_MS = [10000, 30000];
const FIRST_LOOK_BACK_MS = 60000;
const ACTIVITY = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'mousemove', 'focus'];
const PRESENCE_MAX_MS = 60000;
const PRESENCE_PER_ITEM = 10;
const ITEM_KEY_RE = /^[1-9]\d{0,9}$/;
const WHO = new Set(['office', 'link']);
const CHAT = new Set(['client', 'trade']);

// v0.278.0 — the poll says how long each entry lasts (ms, by the server's
// clock); the page keeps its own deadline from that, so a skewed clock on the
// reader's device can never keep a ghost "editing". readPresence: an answer →
// { id: [{ name, who, chat, until, typingUntil }] }; presenceAt: who is there
// at `t` → { id: [{ name, typing, who, chat }] }. who / chat are null when the
// server did not say (an older server).
export function readPresence(raw, t) {
  const out = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const [k, list] of Object.entries(raw)) {
    if (!ITEM_KEY_RE.test(k) || !Array.isArray(list)) continue;
    const people = [];
    for (const e of list) {
      if (people.length >= PRESENCE_PER_ITEM) break;
      const ms = Number(e && e.ms);
      if (!e || typeof e.name !== 'string' || !(ms > 0)) continue;
      const typingMs = Math.max(0, Number(e.typingMs) || 0);
      // v0.313.0 - the office is told which link a person on the trades' chat
      // came in by, to show them on that subbie's tab only.
      const linkId = Number(e.linkId);
      people.push({
        name: e.name.slice(0, 30),
        who: WHO.has(e.who) ? e.who : null,
        chat: CHAT.has(e.chat) ? e.chat : null,
        ...(e.who === 'link' && Number.isInteger(linkId) && linkId > 0 ? { linkId } : null),
        until: t + Math.min(ms, PRESENCE_MAX_MS),
        typingUntil: t + Math.min(typingMs, ms, PRESENCE_MAX_MS),
      });
    }
    if (people.length) out[k] = people;
  }
  return out;
}

export function presenceAt(map, t) {
  const out = {};
  for (const [k, list] of Object.entries(map || {})) {
    const here = (list || []).filter((e) => e.until > t)
      .map((e) => ({ name: e.name, typing: e.typingUntil > t, who: e.who || null, chat: e.chat || null, ...(e.linkId ? { linkId: e.linkId } : null) }));
    if (here.length) out[k] = here;
  }
  return out;
}

// Which items changed between the last answer (`prev`, defectId → n; null on
// the first answer) and this one.
export function changedItems(prev, items, since) {
  const out = [];
  for (const [k, it] of Object.entries(items || {})) {
    const id = Number(k);
    if (!Number.isFinite(id) || !it) continue;
    if (prev == null) {
      if (Number(it.at) >= since - FIRST_LOOK_BACK_MS) out.push(id);
    } else if (prev[k] !== it.n) {
      out.push(id);
    }
  }
  return out;
}

export function watchLive({
  pass,
  onChange,
  onState = () => {},
  onPresence = null,
  getPass = null,
  since = Date.now(),
  intervalMs = LIVE_POLL_MS,
  idleMs = LIVE_IDLE_MS,
  fetchImpl = (typeof fetch === 'function' ? fetch.bind(globalThis) : null),
  doc = hasDoc ? document : null,
  win = hasWin ? window : null,
  now = () => Date.now(),
  setTimer = setTimeout,
  clearTimer = clearTimeout,
} = {}) {
  let k = pass;
  let v = '';
  let last = null;
  let lastActivity = now();
  let failures = 0;
  let renewed = false;
  let timer = null;
  let inFlight = false;
  let stopped = false;
  let state = null;
  let presence = {};
  let presenceKey = '{}';

  function emitPresence() {
    if (!onPresence) return;
    const view = presenceAt(presence, now());
    const key = JSON.stringify(view);
    if (key === presenceKey) return;
    presenceKey = key;
    try { onPresence(view); } catch (_) {}
  }
  function setState(s) {
    if (s === state) return;
    state = s;
    try { onState(s); } catch (_) {}
    if (s !== 'live') { presence = {}; emitPresence(); }
  }
  function schedule(ms) {
    if (stopped) return;
    if (timer != null) clearTimer(timer);
    timer = setTimer(poll, ms);
  }
  function end(s) {
    setState(s);
    stop();
  }

  async function poll() {
    timer = null;
    if (stopped || inFlight) return;
    if (doc && doc.hidden) return;               // starts again on visibilitychange
    if (now() - lastActivity > idleMs) { setState('paused'); return; }
    inFlight = true;
    let res = null;
    let json = null;
    try {
      res = await fetchImpl(`/api/live-poll?k=${encodeURIComponent(k)}&v=${encodeURIComponent(v)}`, { cache: 'no-store', credentials: 'same-origin' });
      if (res && res.ok) json = await res.json();
    } catch (_) {
      res = null;
    }
    inFlight = false;
    if (stopped) return;

    if (res && res.status === 401) {
      if (getPass && !renewed) {
        renewed = true;
        let next = null;
        try { next = await getPass(); } catch (_) { next = null; }
        if (stopped) return;
        if (next) { k = next; v = ''; schedule(0); return; }
      }
      end('ended');
      return;
    }
    if (res && res.status === 503) { end('off'); return; }
    if (!res || !res.ok || !json) {
      failures += 1;
      setState('retrying');
      schedule(LIVE_BACKOFF_MS[Math.min(failures, LIVE_BACKOFF_MS.length) - 1]);
      return;
    }

    failures = 0;
    renewed = false;
    if (!json.same) {
      const items = json.items || {};
      const changed = changedItems(last, items, since);
      const seen = {};
      for (const [id, it] of Object.entries(items)) if (it) seen[id] = it.n;
      last = seen;
      if (onPresence) presence = readPresence(json.presence, now());
      if (changed.length) { try { onChange(changed); } catch (_) {} }
    }
    v = json.v || '';
    setState('live');
    emitPresence();                              // an entry can run out between answers
    schedule(intervalMs);
  }

  function activity() {
    lastActivity = now();
    if (state === 'paused' && timer == null && !inFlight && !stopped) { setState('live'); poll(); }
  }
  function onVisibility() {
    if (!doc || doc.hidden || stopped) return;
    lastActivity = now();
    if (!inFlight) { if (timer != null) clearTimer(timer); timer = null; poll(); }
  }
  function onOnline() {
    if (stopped || inFlight) return;
    if (timer != null) clearTimer(timer);
    timer = null;
    poll();
  }

  function stop() {
    stopped = true;
    if (timer != null) clearTimer(timer);
    timer = null;
    if (doc) doc.removeEventListener('visibilitychange', onVisibility);
    if (win) {
      ACTIVITY.forEach((e) => win.removeEventListener(e, activity, true));
      win.removeEventListener('online', onOnline);
    }
  }

  if (!k || !fetchImpl) {
    setState('off');
    return { stop() {}, pollNow() {} };
  }
  if (doc) doc.addEventListener('visibilitychange', onVisibility);
  if (win) {
    ACTIVITY.forEach((e) => win.addEventListener(e, activity, { capture: true, passive: true }));
    win.addEventListener('online', onOnline);
  }
  schedule(0);
  return {
    stop,
    pollNow() { if (!stopped && !inFlight) { lastActivity = now(); schedule(0); } },
  };
}

// presenceBeacon — the office's half (v0.278.0). While an item is open on the
// office's screen it tells /api/live-presence "on this item" every 15 s, and
// typing() says "typing a reply" (at most every 5 s; it stops 6 s after the
// last key, or at once with typing(false) when the message is sent). It goes
// quiet, and says it has left, when the tab is hidden, nobody has touched
// the page for 90 s, the office pauses sending (setEnabled(false)), the item
// closes (stop()) or the page goes. A refused pass (401) or no live set up
// (503) ends it. Blob only: never the database.
//
// v0.284.0 — `fields` go in every beat: the office sends { chat } (the tab on
// screen), a link page { d, g, chat?, name? } (the item in front, its tag, and
// the chat for a both-chats link). setFields(next) moves the beacon: a new
// item says "left" on the old one first (with the old fields), then beats the
// new one at once; a chat-only change just beats at once.
//
//   presenceBeacon({ pass, enabled, fields }) → { typing(on = true), setEnabled(bool), setFields(obj), stop() }
export const PRESENCE_BEAT_MS = 15000;
export const PRESENCE_TOUCH_MS = 90000;
const TYPING_EVERY_MS = 5000;
const TYPING_IDLE_MS = 6000;

export function presenceBeacon({
  pass,
  enabled: startEnabled = true,
  fields: startFields = {},
  beatMs = PRESENCE_BEAT_MS,
  touchMs = PRESENCE_TOUCH_MS,
  fetchImpl = (typeof fetch === 'function' ? fetch.bind(globalThis) : null),
  doc = hasDoc ? document : null,
  win = hasWin ? window : null,
  now = () => Date.now(),
  setTimer = setTimeout,
  clearTimer = clearTimeout,
} = {}) {
  let enabled = false;
  let stopped = false;
  let dead = !pass || !fetchImpl;
  let timer = null;
  let idleTimer = null;
  let lastTouch = now();
  let typingOn = false;
  let typingSentAt = -Infinity;
  let here = false;                              // the server has us on the item
  let fields = { ...(startFields || {}) };

  function send(extra, keepalive = false) {
    if (dead) return;
    let p = null;
    try {
      p = fetchImpl('/api/live-presence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ p: pass, ...fields, ...extra }),
        credentials: 'same-origin',
        cache: 'no-store',
        keepalive,
      });
    } catch (_) { return; }
    if (p && typeof p.then === 'function') {
      p.then((r) => { if (r && (r.status === 401 || r.status === 503)) quit(); }, () => {});
    }
  }
  const visible = () => !(doc && doc.hidden);
  const active = () => enabled && !stopped && !dead && visible() && now() - lastTouch <= touchMs;
  function clearBeat() { if (timer != null) clearTimer(timer); timer = null; }
  function clearIdle() { if (idleTimer != null) clearTimer(idleTimer); idleTimer = null; }
  function schedule() { clearBeat(); if (!dead && !stopped) timer = setTimer(beat, beatMs); }

  function beat() {
    timer = null;
    if (!active()) { leave(); return; }
    send({ typing: typingOn });
    here = true;
    schedule();
  }
  function leave() {
    clearBeat();
    clearIdle();
    typingOn = false;
    if (here) { here = false; send({ leaving: true }, true); }
  }
  function typingStopped() {
    idleTimer = null;
    if (!typingOn) return;
    typingOn = false;
    if (active()) { send({ typing: false }); here = true; schedule(); }
  }
  function typing(on = true) {
    if (on === false) { clearIdle(); typingStopped(); return; }
    lastTouch = now();
    if (!active()) return;
    clearIdle();
    idleTimer = setTimer(typingStopped, TYPING_IDLE_MS);
    if (typingOn && now() - typingSentAt < TYPING_EVERY_MS) return;
    typingOn = true;
    typingSentAt = now();
    send({ typing: true });
    here = true;
    schedule();
  }
  function touch() {
    lastTouch = now();
    if (timer == null && active()) beat();
  }
  function onVisibility() {
    if (stopped || dead) return;
    if (!visible()) { leave(); return; }
    touch();
  }
  function onPageHide() { leave(); }

  function unlisten() {
    if (doc) doc.removeEventListener('visibilitychange', onVisibility);
    if (win) {
      ACTIVITY.forEach((e) => win.removeEventListener(e, touch, true));
      win.removeEventListener('pagehide', onPageHide);
    }
  }
  function quit() {
    dead = true;
    here = false;
    clearBeat();
    clearIdle();
    unlisten();
  }
  function setEnabled(on) {
    const b = !!on;
    if (stopped || dead || b === enabled) return;
    enabled = b;
    if (b) { lastTouch = now(); beat(); } else leave();
  }
  function setFields(next) {
    const n = { ...(next || {}) };
    if (stopped || dead || JSON.stringify(n) === JSON.stringify(fields)) return;
    if (n.d !== fields.d) leave();               // "left" goes with the old item's fields
    clearIdle();
    typingOn = false;                            // words in the old chat are not typing in this one
    fields = n;
    if (active()) { clearBeat(); beat(); }
  }
  function stop() {
    if (stopped) return;
    leave();
    stopped = true;
    unlisten();
  }

  if (!dead) {
    if (doc) doc.addEventListener('visibilitychange', onVisibility);
    if (win) {
      ACTIVITY.forEach((e) => win.addEventListener(e, touch, { capture: true, passive: true }));
      win.addEventListener('pagehide', onPageHide);
    }
    setEnabled(startEnabled);
  }
  return { typing, setEnabled, setFields, stop };
}
